import { useState } from "react";

interface FAQItem {
  q: string;
  a: string;
}

const faqs: FAQItem[] = [
  {
    q: "What is the Minimum Order Quantity (MOQ)?",
    a: "MOQ is flexible and parts-dependent. For high-fault categories such as braking systems, filters, ignition coils, and suspension components, we accommodate smaller initial orders to allow quality verification. For strategic commodities (lubricants, bulk consumables), standard international pallet or FCL minimums apply. Contact us with your specific OE numbers for a tailored MOQ structure.",
  },
  {
    q: "What are your lead times and quote turnaround?",
    a: "We operate a 24-hour quote service for all standard OE part number inquiries. Pricing sheets with availability and MOQ data are delivered within 48 business hours. Delivery lead times are customized based on your destination, freight mode (FCL/LCL/Rail/Road), and order volume. Expedited fulfillment options are available for high-priority procurement.",
  },
  {
    q: "What payment terms do you accept?",
    a: "We operate on standard international commercial terms including T/T (Telegraphic Transfer), L/C (Letter of Credit) for large-volume orders, and D/P (Documents against Payment). All transactions are governed by legally binding international commercial contracts drafted in accordance with CISG standards. Payment terms are negotiated per contract based on order volume and relationship history.",
  },
  {
    q: "What shipping documentation do you provide?",
    a: "Every shipment is accompanied by a complete documentation package: Commercial Invoice, Packing List, Bill of Lading (or Air Waybill), Certificate of Origin, and where applicable, EAC Certificates of Conformity for EAEU market entry. All customs declarations are 100% white — no undervaluation, no grey-channel documentation.",
  },
  {
    q: "How do you verify OE part quality and authenticity?",
    a: "Our quality verification pipeline is three-stage: (1) Cross-reference against our 千万级 (tens-of-millions) domestic distribution data stream to identify high-volume, high-trust suppliers; (2) Real installation feedback analysis — we track actual vehicle fitment data, not just catalog specs; (3) Factory audit — all partner factories must be 100% white customs cleared with verifiable ISO certification. We do not list parts that have not passed all three stages.",
  },
  {
    q: "Which European vehicle brands and model years do you cover?",
    a: "Our core coverage spans European-specification vehicles from 2004 to 2024, with primary focus on Mercedes-Benz, BMW, Audi, Volkswagen, Porsche, Jaguar, and Land Rover. Coverage includes both petrol and diesel variants across sedan, SUV, and commercial vehicle segments. OE part number cross-referencing is available for all listed brands.",
  },
  {
    q: "Do you handle EAEU / Russian market compliance?",
    a: "Yes. Our XYLP entity in Russia provides localized EAEU compliance, EAC certification support, and direct distribution within the Russian Federation and CIS markets. All products destined for EAEU markets are processed through our Moscow compliance hub to ensure full regulatory adherence.",
  },
  {
    q: "What logistics routes do you offer?",
    a: "We offer comprehensive multi-modal logistics: Ocean Freight (FCL & LCL) via Guangzhou Port and Ningbo Port; Overland Trucking networks initiating from Guangzhou and Hangzhou; Cross-border China-Europe Railway Express (中欧班列) for Central Asia and European destinations; and dedicated road freight for CIS/EAEU last-mile delivery. All routes are supported by full customs documentation.",
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section
      id="faq"
      className="py-28 px-6"
      style={{ background: "#0d1525" }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8"
            style={{ background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.35)" }}
          >
            <i className="ri-question-answer-line text-xs" style={{ color: "#F59E0B" }} />
            <span className="text-xs tracking-widest uppercase font-medium" style={{ color: "#F59E0B", letterSpacing: "0.2em" }}>
              Global Procurement FAQ
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-thin mb-6 text-white" style={{ letterSpacing: "0.05em" }}>
            GLOBAL PROCUREMENT
            <br />
            <span style={{ color: "#F59E0B" }}>FAQ.</span>
          </h2>
          <p className="text-base font-light max-w-xl mx-auto" style={{ color: "rgba(255,255,255,0.55)", lineHeight: "1.85" }}>
            Standard B2B procurement terms, quality verification, and logistics details.
            All answers reflect our actual operational standards.
          </p>
        </div>

        {/* FAQ Items */}
        <div className="flex flex-col gap-3">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className="rounded-xl overflow-hidden transition-all duration-300"
              style={{
                background: openIndex === i ? "rgba(245,158,11,0.06)" : "rgba(255,255,255,0.04)",
                border: openIndex === i ? "1px solid rgba(245,158,11,0.35)" : "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <button
                onClick={() => toggle(i)}
                className="w-full flex items-center justify-between px-7 py-5 text-left cursor-pointer"
              >
                <span
                  className="text-sm font-medium pr-6"
                  style={{ color: openIndex === i ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.7)", letterSpacing: "0.02em" }}
                >
                  {faq.q}
                </span>
                <div
                  className="w-7 h-7 flex items-center justify-center rounded-full shrink-0 transition-all duration-300"
                  style={{
                    background: openIndex === i ? "rgba(245,158,11,0.15)" : "rgba(255,255,255,0.06)",
                    border: openIndex === i ? "1px solid rgba(245,158,11,0.4)" : "1px solid rgba(255,255,255,0.12)",
                    transform: openIndex === i ? "rotate(45deg)" : "rotate(0deg)",
                  }}
                >
                  <i className="ri-add-line text-sm" style={{ color: openIndex === i ? "#F59E0B" : "rgba(255,255,255,0.4)" }} />
                </div>
              </button>

              {openIndex === i && (
                <div className="px-7 pb-6">
                  <div className="mb-4" style={{ height: "1px", background: "rgba(245,158,11,0.2)" }} />
                  <p className="text-sm font-light leading-relaxed" style={{ color: "rgba(255,255,255,0.6)", lineHeight: "1.9" }}>
                    {faq.a}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA */}
        <div
          className="mt-14 p-8 rounded-2xl text-center"
          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(245,158,11,0.2)" }}
        >
          <p className="text-sm font-light mb-4" style={{ color: "rgba(255,255,255,0.55)" }}>
            Don&apos;t see your question? Our B2B team responds to all inquiries within 12 business hours.
          </p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-xs font-bold tracking-widest transition-all duration-200 hover:brightness-110 whitespace-nowrap cursor-pointer text-white"
            style={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)", letterSpacing: "0.14em" }}
          >
            <i className="ri-mail-send-line text-sm" />
            SUBMIT YOUR INQUIRY
          </a>
        </div>
      </div>
    </section>
  );
}
